/* ===== Documents: upload a file and tag it ===== */

import { useState } from 'react'
import type { TenantContext } from '@zetyar/data-contracts'
import type { DocumentCategory } from '@zetyar/domain'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Modal } from '@/presentation/components/ui/Modal'
import { webDocumentRepository } from '@/infrastructure/repositories/documents'
import {
  ALLOWED_DOCUMENT_TYPES,
  MAX_DOCUMENT_BYTES,
  assertDocumentFileAllowed,
  formatFileSize,
  uploadOrganizationDocument,
} from '@/infrastructure/storage/documents'

const FIELD_CLASS =
  'h-11 w-full rounded-[13px] border border-neutral-200 bg-white px-3.5 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100'

const NEW_TAG = '__new__'

interface DocumentUploadModalProps {
  readonly context: TenantContext
  readonly categories: readonly DocumentCategory[]
  readonly onClose: () => void
  readonly onUploaded: () => void
}

function uploadErrorMessage(error: unknown): string {
  const message = typeof error === 'object' && error !== null && 'message' in error ? String((error as { message: unknown }).message) : ''
  if (message.includes('documents_forbidden') || message.toLowerCase().includes('row-level security') || message.includes('Unauthorized')) {
    return 'Tu rol no permite subir documentos en esta organización.'
  }
  if (message.includes('documents_invalid_tag')) return 'La etiqueta no es válida.'
  return 'No se pudo subir el documento. Intenta nuevamente.'
}

export function DocumentUploadModal({ context, categories, onClose, onUploaded }: DocumentUploadModalProps) {
  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [newCategoryName, setNewCategoryName] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const pickFile = (selected: File | undefined) => {
    setError(null)
    if (!selected) {
      setFile(null)
      setFileError(null)
      return
    }
    try {
      assertDocumentFileAllowed(selected)
      setFile(selected)
      setFileError(null)
      // Suggest the file name (without extension) as the title, only while the title is untouched.
      setTitle((current) => current || selected.name.replace(/\.[^.]+$/, ''))
    } catch (caught) {
      setFile(null)
      setFileError(caught instanceof Error ? caught.message : 'Archivo no permitido.')
    }
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    const create = webDocumentRepository.create
    const createCategory = webDocumentRepository.createCategory
    if (!create || !file || isSaving) return
    if (!title.trim()) {
      setError('Ingresa un título para el documento.')
      return
    }

    setIsSaving(true)
    setError(null)
    try {
      let finalCategoryId: string | undefined = categoryId && categoryId !== NEW_TAG ? categoryId : undefined
      if (categoryId === NEW_TAG && newCategoryName.trim() && createCategory) {
        const created = await createCategory(context, newCategoryName.trim())
        finalCategoryId = created.id
      }

      const objectPath = await uploadOrganizationDocument({ organizationId: context.organizationId, file })
      await create(context, {
        title: title.trim(),
        description: description.trim() || undefined,
        fileUrl: objectPath,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        categoryId: finalCategoryId,
      })
      onUploaded()
    } catch (caught) {
      console.error('Error al subir el documento:', caught)
      setError(uploadErrorMessage(caught))
      setIsSaving(false)
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!isSaving) onClose()
      }}
      title="Subir documento"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button type="submit" form="document-upload-form" isLoading={isSaving} disabled={!file}>
            Subir documento
          </Button>
        </>
      }
    >
      <form id="document-upload-form" className="space-y-4" onSubmit={submit}>
        <label className="flex flex-col gap-1.5 text-sm font-semibold text-neutral-700 dark:text-neutral-200">
          Archivo
          <input
            type="file"
            accept={ALLOWED_DOCUMENT_TYPES.join(',')}
            onChange={(event) => pickFile(event.target.files?.[0])}
            className="text-sm text-neutral-600 dark:text-neutral-300"
          />
          <span className="text-xs font-normal text-neutral-500 dark:text-neutral-400">
            PDF, imagen, documento de Office, CSV o texto. Máximo {Math.round(MAX_DOCUMENT_BYTES / 1024 / 1024)} MB.
          </span>
        </label>
        {fileError && <p role="alert" className="text-sm text-danger-600 dark:text-danger-300">{fileError}</p>}
        {file && (
          <p className="rounded-[13px] bg-neutral-50 px-3 py-2 text-xs text-neutral-600 dark:bg-neutral-800/60 dark:text-neutral-300">
            {file.name} · {formatFileSize(file.size)}
          </p>
        )}

        <Input label="Título" maxLength={160} value={title} onChange={(event) => setTitle(event.target.value)} />
        <Input
          label="Descripción (opcional)"
          maxLength={500}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />

        <label className="flex flex-col gap-1.5 text-sm font-semibold text-neutral-700 dark:text-neutral-200">
          Etiqueta
          <select value={categoryId} onChange={(event) => setCategoryId(event.target.value)} className={FIELD_CLASS}>
            <option value="">Sin etiqueta</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
            <option value={NEW_TAG}>Crear una etiqueta nueva</option>
          </select>
        </label>
        {categoryId === NEW_TAG && (
          <Input
            label="Nombre de la etiqueta"
            maxLength={60}
            placeholder="Ej: Actas, Reglamentos, Convenios"
            value={newCategoryName}
            onChange={(event) => setNewCategoryName(event.target.value)}
          />
        )}

        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          El archivo se guarda en el almacenamiento privado de la organización y solo se descarga con un enlace temporal.
        </p>
        {error && <p role="alert" className="text-sm text-danger-600 dark:text-danger-300">{error}</p>}
      </form>
    </Modal>
  )
}
